// OS-level (system) notifications + a short chime for the task status feed.
// Uses the browser Notification API, so it needs the user's permission and a
// secure origin; everything degrades to a no-op when unavailable.

let audioCtx: AudioContext | null = null;
let armed = false;

const supported = () => typeof window !== "undefined" && "Notification" in window;

function getAudio(): AudioContext | null {
  try {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    if (!audioCtx) audioCtx = new Ctor();
    return audioCtx;
  } catch {
    return null;
  }
}

/** Three-tone chime generated with WebAudio, so no sound asset is needed. */
export function playNotificationSound() {
  const ctx = getAudio();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const start = ctx.currentTime;
    [880, 1318.5, 1760].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t0 = start + i * 0.16;
      osc.type = "triangle"; // brighter and louder than a sine
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(1, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.45);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.5);
    });
  } catch { /* ignore */ }
}

/**
 * Browsers only allow the permission prompt and audio after a user gesture,
 * so arm both on the first click / key press anywhere in the app.
 */
export function armSystemNotifications() {
  if (armed || !supported()) return;
  armed = true;
  const unlock = () => {
    // Stays registered: browsers may re-suspend the audio context, and any
    // later gesture un-suspends it so the chime keeps working.
    const ctx = getAudio();
    if (ctx && ctx.state !== "running") ctx.resume().catch(() => {});
    if (Notification.permission === "default") {
      try { Notification.requestPermission().catch(() => {}); } catch { /* old Safari */ }
    }
  };
  document.addEventListener("click", unlock, true);
  document.addEventListener("keydown", unlock, true);
}

type SystemNotice = { title: string; body: string; tag: string; onClick?: () => void };

/** Show an OS notification (when permitted) and always play the chime. */
export function notifySystem({ title, body, tag, onClick }: SystemNotice) {
  playNotificationSound();
  if (!supported() || Notification.permission !== "granted") return;
  try {
    // `tag` makes the same event from several open tabs collapse into one.
    const n = new Notification(title, { body, tag, silent: true, icon: "/favicon.ico" });
    n.onclick = () => {
      window.focus();
      onClick?.();
      n.close();
    };
  } catch { /* some mobile browsers only allow ServiceWorker notifications */ }
}
